import { AnimatePresence, motion } from "motion/react";
import { cn } from "@/modern/utils";
import type { SurfaceProgress, SurfaceTab } from "../api/types";
import type { ActionFlags } from "../utils/actionMatrix";
import PromoteSurfaceSelectionBar from "./PromoteSurfaceSelectionBar";

type Props = {
  activeTab: SurfaceTab;
  onChange: (tab: SurfaceTab) => void;
  progress?: SurfaceProgress;
  loadedCount?: number;
  onSelectAllLoaded?: () => void;
  selection?: {
    count: number;
    actions: ActionFlags;
    busy?: boolean;
    onAccept: () => void;
    onReject: () => void;
    onSkip: () => void;
    onClear: () => void;
  } | null;
};

function tabCount(tab: SurfaceTab, progress?: SurfaceProgress): number | null {
  if (!progress) return null;
  if (tab === "review") return progress.pending_review ?? 0;
  if (tab === "skipped") return progress.skipped ?? 0;
  if (tab === "accepted") return progress.accepted ?? null;
  if (tab === "rejected") return progress.rejected ?? null;
  return null;
}

const SEGMENTS: { id: SurfaceTab; label: string }[] = [
  { id: "review", label: "Review" },
  { id: "skipped", label: "Skip" },
  { id: "accepted", label: "Accepted" },
  { id: "rejected", label: "Rejected" },
];

/**
 * Floating control island — tabs when idle, expands for selection actions.
 * Select-all / selected count live inside this dock (no overlay, no extra row).
 */
export default function PromoteSurfaceTabs({
  activeTab,
  onChange,
  progress,
  loadedCount = 0,
  onSelectAllLoaded,
  selection,
}: Props) {
  const selecting = Boolean(selection && selection.count > 0);
  const selectedCount = selection?.count ?? 0;
  const showSelectAll =
    Boolean(onSelectAllLoaded) && loadedCount > 1 && selectedCount < loadedCount;
  const showSelectedCount = selecting && selectedCount > 0;
  const showSelectMeta = showSelectAll || showSelectedCount;

  return (
    <div className="relative z-20 shrink-0 px-2.5 pb-1.5 pt-2.5 sm:px-3 sm:pt-3">
      <motion.div
        layout
        transition={{ type: "spring", stiffness: 420, damping: 34, mass: 0.85 }}
        className={cn(
          "mx-auto flex h-11 w-fit max-w-full items-center rounded-full bg-card/95 backdrop-blur-md sm:h-12",
          "shadow-[0_8px_30px_-8px_rgba(0,0,0,0.55)] ring-1 ring-foreground/10",
          selecting && "ring-primary/25",
          showSelectMeta || selecting
            ? "gap-1.5 px-1.5 sm:gap-2 sm:px-2"
            : "p-0"
        )}
      >
        <div
          role="tablist"
          aria-label="Promote queue filters"
          className="flex shrink-0 items-center rounded-full bg-muted/70 p-0.5"
        >
          {SEGMENTS.map((tab) => {
            const active = activeTab === tab.id;
            const count = tabCount(tab.id, progress);
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => onChange(tab.id)}
                className={cn(
                  "relative flex items-center justify-center gap-1 rounded-full px-2.5 py-1.5 text-xs transition-colors sm:gap-1.5 sm:px-3 sm:text-sm",
                  active
                    ? "font-semibold text-primary"
                    : "font-medium text-muted-foreground hover:text-foreground"
                )}
              >
                {active && (
                  <motion.span
                    layoutId="promote-surface-tab-pill"
                    className="absolute inset-0 rounded-full border border-primary bg-background shadow-sm"
                    transition={{ type: "spring", stiffness: 480, damping: 36 }}
                  />
                )}
                <span className="relative truncate">{tab.label}</span>
                {count != null && (
                  <span
                    className={cn(
                      "relative min-w-[1.25rem] rounded-full px-1.5 py-0.5 text-center text-[10px] tabular-nums",
                      active
                        ? "font-bold text-primary"
                        : "font-semibold text-muted-foreground"
                    )}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {showSelectMeta && (
          <>
            <div className="h-5 w-px shrink-0 bg-foreground/10" aria-hidden />
            <div className="flex shrink-0 items-center gap-2 px-1">
              {showSelectedCount && (
                <span className="text-xs tabular-nums text-muted-foreground" aria-live="polite">
                  <span className="font-semibold text-foreground">{selectedCount}</span>
                  <span className="ml-1 hidden sm:inline">selected</span>
                </span>
              )}
              {showSelectAll && (
                <button
                  type="button"
                  onClick={onSelectAllLoaded}
                  className="whitespace-nowrap text-xs font-medium text-muted-foreground transition hover:text-foreground"
                >
                  Select all
                  <span className="ml-1 tabular-nums opacity-70">{loadedCount}</span>
                </button>
              )}
            </div>
          </>
        )}

        <AnimatePresence initial={false} mode="popLayout">
          {selecting && selection ? (
            <motion.div
              key="actions"
              layout
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.15 }}
              className="flex shrink-0 items-center gap-1.5 sm:gap-2"
            >
              <div className="h-5 w-px shrink-0 bg-foreground/10" aria-hidden />
              <PromoteSurfaceSelectionBar
                actions={selection.actions}
                busy={selection.busy}
                skipLabel={activeTab === "review" ? "Skip" : "To Skip"}
                onAccept={selection.onAccept}
                onReject={selection.onReject}
                onSkip={selection.onSkip}
                onClear={selection.onClear}
              />
            </motion.div>
          ) : null}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
